import { useCallback, useEffect, useRef, useState } from "react";
import { ERROR_LOADING_ALL_SCHEDULES, SCHEDULE_POLL_INTERVAL_MS } from "constants/schedule-monitor.constants";
import StatusCodes from "enums/status-code.enums";
import SchedulesRepository from "repositories/schedules/schedules.repository.impl";
import { IScheduleRowViewModel } from "types/schedule";

interface IUseAllSchedulesOptions {
    onForbidden?: () => void;
    onNotFound?: () => void;
}

interface IUseAllSchedulesResult {
    rows: IScheduleRowViewModel[];
    loading: boolean;
    error: string | null;
    /**
     * Bumped on every successful drain, including background polls that return an identical
     * row set. The KPI cards' task-data fetch keys off this (see `use-latest-task-by-schedule-id.ts`)
     * so it re-runs on the 60s poll even when the schedule id set itself hasn't changed.
     */
    refreshedAt: number;
    lastRefreshedAt: Date | null;
    refresh: () => void;
}

/**
 * Drains every schedule across all pages, independent of the paginated table's own fetch -
 * this is the complete set that downstream KPI calculations need. The drain is abortable, and
 * since `getAllSchedules` only resolves once the whole set has been collected, an abort simply
 * discards the in-progress promise: no partial page is ever written to state.
 */
const useAllSchedules = (options?: IUseAllSchedulesOptions): IUseAllSchedulesResult => {
    const { onForbidden, onNotFound } = options ?? {};
    const [rows, setRows] = useState<IScheduleRowViewModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshedAt, setRefreshedAt] = useState(0);
    const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

    const abortControllerRef = useRef<AbortController | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const drain = useCallback((fetchOptions?: { background?: boolean }) => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        if (!fetchOptions?.background) {
            setLoading(true);
            setError(null);
        }

        SchedulesRepository.getAllSchedules({ signal: controller.signal })
            .then((drained) => {
                if (controller.signal.aborted) return;
                setRows(drained);
                setError(null);
                setLoading(false);
                setRefreshedAt(Date.now());
                setLastRefreshedAt(new Date());
            })
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            .catch((thrown: any) => {
                if (controller.signal.aborted) return;
                const responseStatus = thrown?.response?.status;
                if (responseStatus === StatusCodes.FORBIDDEN) {
                    onForbidden?.();
                    return;
                }
                if (responseStatus === StatusCodes.NOT_FOUND) {
                    onNotFound?.();
                    return;
                }
                // A failed background poll leaves the previous drain (and KPI cards) on screen.
                if (!fetchOptions?.background) setError(ERROR_LOADING_ALL_SCHEDULES);
                setLoading(false);
            });
    }, [onForbidden, onNotFound]);

    const schedulePoll = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            if (document.hidden) return;
            drain({ background: true });
        }, SCHEDULE_POLL_INTERVAL_MS);
    }, [drain]);

    useEffect(() => {
        drain();
        schedulePoll();
        return () => {
            abortControllerRef.current?.abort();
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A manual refresh is an explicit "I want the latest now" - it should not be followed
    // moments later by an auto-refresh, so the poll countdown restarts from here.
    const refresh = useCallback(() => {
        drain();
        schedulePoll();
    }, [drain, schedulePoll]);

    return { rows, loading, error, refreshedAt, lastRefreshedAt, refresh };
};

export default useAllSchedules;
